<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Creating_the_Metamodel';
    let nextLink= '/Documentation/Creating_the_Metamodel/Defining_Properties';
    
    import Note from "$lib/notes/Note.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="the-language-structure-1"
  bind:intersecting={visibleById["the-language-structure-1"]}
  onIntersectChange={(visible) => setVisible("the-language-structure-1", visible)}
>The Language Structure</SectionComponent>
<SectionComponent tag="h2" 
  id="model-2"
  bind:intersecting={visibleById["model-2"]}
  onIntersectChange={(visible) => setVisible("model-2", visible)}
>Model</SectionComponent>
<p>A <em>model</em> is the root of the abstract syntax tree.
It may hold any number of model units as children. These model units may be of different types. For instance, you
can have model units that define the items in a home automation system, and other model units that define the rules
that apply in this system.</p>
<Note> 
{#snippet header()} Models are never explicit in an editor{/snippet}
{#snippet content()}
The model is never shown in an editor as a whole.
It is always shown in parts: the model units. However, the provided web app does 'show' the model and the units, that are
part of it, in its left panel.
{/snippet}
</Note>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L7-L10

model InsuranceModel &#123;
    parts: Part[];              // units that hold partial definitions of insurance products
    products: Product[];        // units that hold sellable insurance products
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="model-unit-3"
  bind:intersecting={visibleById["model-unit-3"]}
  onIntersectChange={(visible) => setVisible("model-unit-3", visible)}
>Model unit</SectionComponent>
<p>A <em>model unit</em> is a part of the model that can be edited by the user independently of the rest of the model. A model unit is
always a direct child of a model. Model units may not extend other units, or implement interfaces.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L12-L15

modelunit Part &#123;
    part: BaseProduct;          // one collection of partial insurance products
    file-extension = "base";    // the file extension used by the parser
&#125;</code></pre>
<p>Model units have one special entry called <code>file-extension</code>, as shown in the example above. This is an optional
indication of the file type that the generated parser will associate with this model unit, i.e. an
instance of the above model unit will be exported/imported to/from a file with extension ‘.base’.</p>
<SectionComponent tag="h2" 
  id="concept-4"
  bind:intersecting={visibleById["concept-4"]}
  onIntersectChange={(visible) => setVisible("concept-4", visible)}
>Concept</SectionComponent>
<p>A <em>concept</em> is the basic element of your language definition. It defines which instances can be present in
a model created by your users.</p>
<p>Concepts may extend one other concept using the keyword <code>base</code>, and implement multiple interfaces.
Furthermore, they may be <em>abstract</em>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L24-L28

concept BaseProduct &#123;
    name: identifier;               // internal name
    isUnderConstruction: boolean;   // defines whether this base product is still 'raw'
    theme: InsuranceTheme;          // the 'kind' of insurance
    parts: InsurancePart[];         // all parts of this product</code></pre>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L106-L112

    Percentage;
&#125;

limited NumberType implements NamedType &#123;
    Number;
&#125;
</code></pre>
<SectionComponent tag="h2" 
  id="expression-concept-5"
  bind:intersecting={visibleById["expression-concept-5"]}
  onIntersectChange={(visible) => setVisible("expression-concept-5", visible)}
>Expression Concept</SectionComponent>
<p>An <em>expression concept</em> is a concept represents an expression. The editor deals differently with these, in
order to give your user a more natural editing experience.</p>
<p>Expression concepts may extend another concept, and implement multiple interfaces.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-expressions.ast#L10-L18

abstract expression Literal base DocuExpression &#123;
&#125;
expression EuroLiteral base Literal &#123;
    euros: number;
    cents: number;
&#125;
expression NumberLiteral base Literal &#123;
    value: number;
&#125;</code></pre>
<Note header={header2} content={content2}> 
{#snippet header2()}Use a Single Root of the Expression AST{/snippet}
{#snippet content2()}
<p>
It is good practice to have all expression concepts inherit from one single root concept. This make it easy 
to have any type of expression as a part of the expression that you want to define.</p>
<p>
For instance, when defining a bracketed expression, i.e. an expression surrounded by brackets, you can 
simply use the root expression concept as type of the property that is to be put between the brackets.
</p>
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="binary-expression-concept-6"
  bind:intersecting={visibleById["binary-expression-concept-6"]}
  onIntersectChange={(visible) => setVisible("binary-expression-concept-6", visible)}
>Binary Expression Concept</SectionComponent>
<p>A <em>binary expression concept</em> is an expression concept that has two sub-expressions, <code>left</code> and <code>right</code> operands,
and an operator, which in the concrete syntax is shown in the middle. For example, the expression <code>4 + 5</code>
has as left operand <code>4</code>, as operator <code>+</code>, and as right operand <code>5</code>.</p>
<p>Any concrete binary expression concept needs to have a priority. For example, in mathematics the
priority of the multiplication is higher than the priority of addition. The expression 5 + 67 * 8
should be read as 5 + (67 * 8), not as (5 + 67) * 8. The priorities are used by Freon to balance the
abstract syntax tree (See <a
  href="/Background/Projectional_Editing#adding-to-an-existing-expression-4"
>Projectional Editing</a>). In
<a href="/Documentation/Defining_an_Editor/Ease_of_Editing">Ease of Editing</a> you can
find more information on how to set the concrete syntax for the operand.</p>
<p>Binary expression concepts may extend one other concept, and implement multiple interfaces.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-expressions.ast#L26-L46

// Basic binary expressions: plus, minus, multiply, divide
abstract binary expression BinaryExpression base DocuExpression &#123;
    left: DocuExpression;
    right: DocuExpression;
&#125;

binary expression PlusExpression base BinaryExpression &#123;
    priority = 4;
&#125;

binary expression MinusExpression base BinaryExpression &#123;
    priority = 4;
&#125;

binary expression MultiplyExpression base BinaryExpression &#123;
    priority = 8;
&#125;

binary expression DivideExpression base BinaryExpression &#123;
    priority = 8;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="limited-concept-7"
  bind:intersecting={visibleById["limited-concept-7"]}
  onIntersectChange={(visible) => setVisible("limited-concept-7", visible)}
>Limited Concept</SectionComponent>
<p>A <em>limited concept</em> defines a fixed set of predefined instances. Actually, it is an extended
version of an enumeration. All instances become part of the standard library of your language.</p>
<p>A limited concept must always have a name property (<code>name: identifier;</code>), but if this is not provided
in the definition then it is automatically created. Furthermore, when a predefined instance does not provide a
value for the name, the name given in the .ast file is used. In this manner, you can define simple enumerations.</p>
<p>Limited concepts may extend another concept, and implement multiple interfaces. Note that the definition of the concept
includes the definition of the predefined instances. For example, the instances of <code>PremiumDays</code> are
<code>Week</code>, <code>Month</code>, <code>Quarter</code>, <code>Semester</code>, and <code>Year</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L129-L146

limited InsuranceTheme &#123;        // limited defined as a simple enumeration
    HomeTheme; HealthTheme; LegalTheme;
&#125;

limited PremiumDays &#123;           // limited with various options
    // if the 'name' property was not provided, it would have been generated
    name: identifier;
    nrOfDays: number;
    // notations 'name:' and '"name":' are both correct
    Week = &#123; name: "Week", nrOfDays: 7 &#125;
    Month = &#123; "name": "Month", nrOfDays: 30 &#125;
    // the following instance gets the name "Quarter"
    Quarter = &#123; nrOfDays: 91 &#125;
    // the following instance gets the name "Semester"
    Semester = &#123; nrOfDays: 182 &#125;
    // the following instance gets the name "Year"
    Year = &#123; nrOfDays: 365 &#125;
&#125;</code></pre>
<Note header={header3} content={content3}> 
{#snippet header3()} No quotes around numbers and booleans.{/snippet}
{#snippet content3()}
For number and boolean types, quotes (double or single) are not allowed around the values of properties of
instances of limited concepts.
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="interface-8"
  bind:intersecting={visibleById["interface-8"]}
  onIntersectChange={(visible) => setVisible("interface-8", visible)}
>Interface</SectionComponent>
<p>An <em>interface</em> is a concept that has no instances. It may extend multiple other interfaces.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L96-L99

// concepts can implement interfaces.
interface NamedType &#123;
    name: identifier;
&#125;</code></pre>
<p>The next section will explain the options for concept properties.</p>


<PrevNextSection {prevLink} {nextLink} />